import React from "react";
import styles from "./AssistantInformation.module.css"
import {Link} from "react-router-dom";
import {TextFieldsLine} from "../../../../../../elements/textFieldsLine/TextFieldsLine";
import {Section} from "../../../../../../elements/section/Section";
import {prepareLocationNameWithId} from "../../../../../location/locationListSlice";
import {FullBotUi, FullLocationUi} from "../../../../../../../api/generated";
import {getRedirectProviderName} from "../../../../modal/createAndUpdate/components/util/redirectProviderUtil";
import {getBotTypeName} from "../../../../modal/createAndUpdate/components/util/botTypeUtil";
import {getSupportedTimezoneLabel} from "../../../../modal/createAndUpdate/components/util/localizationPolicyUtil";
import {getLocaleTagTitle} from "../../../../../../../utils/enumValueTitleUtil";
import {LeftSideTextField} from "../../../../../../elements/leftSideTextField/LeftSideTextField";

interface FranchiseDetailsItem {
    publicId?: string;
    name?: string;
}

interface AssistantInformationProps {
    bot: FullBotUi
}

const formatNameWithId = (name?: string, publicId?: string): string => {
    if (!publicId) {
        return name || "—";
    }

    return prepareLocationNameWithId(name || publicId, publicId);
}

const DetailsLinkList: React.FC<React.PropsWithChildren> = ({children}) => {
    return <span className={styles.detailsLinkList}>{children}</span>
}

export const AssistantInformation: React.FC<AssistantInformationProps> = ({
                                                                              bot
                                                                          }) => {
    const franchiseList = ((bot as FullBotUi & { franchiseList?: FranchiseDetailsItem[] }).franchiseList || []);
    const externalCommunicationsDisabled =
        (bot as FullBotUi & { externalCommunicationsDisabled?: boolean }).externalCommunicationsDisabled ?? false;

    const mapLocations = () => {
        const linkedLocations = (bot.locationList || []).filter(location => location.publicId);

        if (!linkedLocations.length) {
            return "—";
        }

        return <DetailsLinkList>
            {linkedLocations.map((location: FullLocationUi) => (
                <Link
                    key={location.publicId}
                    className={styles.detailsLink}
                    to={`/locations/${location.publicId}`}
                >
                    {formatNameWithId(location.name, location.publicId)}
                </Link>
            ))}
        </DetailsLinkList>
    }

    const mapFranchises = () => {
        const linkedFranchises = franchiseList.filter(franchise => franchise.publicId);

        if (!linkedFranchises.length) {
            return "—";
        }

        return <DetailsLinkList>
            {linkedFranchises.map(franchise => (
                <Link
                    key={franchise.publicId}
                    className={styles.detailsLink}
                    to={`/franchises/${franchise.publicId}`}
                >
                    {formatNameWithId(franchise.name, franchise.publicId)}
                </Link>
            ))}
        </DetailsLinkList>
    }

    return <Section title={"Assistant Information"}>
        <div className={styles.fieldsWrapper}>
            <TextFieldsLine
                leftFieldTitle={"Public ID"}
                leftFieldValue={bot.publicId}
                rightFieldTitle={"Assistant Type"}
                rightFieldValue={getBotTypeName(bot.botType)}
            />
            <TextFieldsLine
                leftFieldTitle={"Franchise(s)"}
                leftFieldValue={mapFranchises()}
                rightFieldTitle={"Location(s)"}
                rightFieldValue={mapLocations()}
            />
            <TextFieldsLine
                leftFieldTitle={"Site URL"}
                leftFieldValue={bot.siteURL}
                rightFieldTitle={"Site Name"}
                rightFieldValue={bot.siteName}
            />
            <TextFieldsLine
                leftFieldTitle={"Group Hash"}
                leftFieldValue={bot.groupHash || "—"}
                rightFieldTitle={"Conversation Limit"}
                rightFieldValue={bot.conversationLimit}
            />
            <LeftSideTextField
                title={"Isolated Mode"}
                value={externalCommunicationsDisabled ? "Enabled" : "Disabled"}
            />
            <TextFieldsLine
                leftFieldTitle={"Working Range"}
                leftFieldValue={bot.workingRange}
                rightFieldTitle={"Redirect Pattern"}
                rightFieldValue={getRedirectProviderName(bot.redirectProvider)}
            />
            <TextFieldsLine
                leftFieldTitle={"Default Locale"}
                leftFieldValue={getLocaleTagTitle(bot.defaultLocale)}
                rightFieldTitle={"Timezone"}
                rightFieldValue={getSupportedTimezoneLabel(bot.timezone)}
            />
            <TextFieldsLine
                leftFieldTitle={"Phone Number Locales"}
                leftFieldValue={bot.supportedPhoneNumberLocales?.join("; ")}
                rightFieldTitle={"Team Information"}
                rightFieldValue={bot.teamInfo || "—"}
            />
        </div>
    </Section>
}
